import React from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

/**
 * Reusable sortable column header.
 * Renders an inline button — clicking cycles asc → desc → none.
 *
 * Props:
 *   field      string identifier for this column
 *   sort       { field, dir } current state
 *   onChange   (next) => void  — caller updates state
 *   children   header label
 *   align      'left' | 'center' | 'right' (default left)
 */
export default function SortableHeader({ field, sort, onChange, children, align = 'left' }) {
  const active = sort?.field === field;
  const dir = active ? sort.dir : null;

  const handleClick = () => {
    let next;
    if (!active) next = { field, dir: 'asc' };
    else if (dir === 'asc') next = { field, dir: 'desc' };
    else next = { field: null, dir: null };
    onChange(next);
  };

  const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  const justify = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`flex items-center gap-1 ${justify} w-full text-left font-semibold text-xs hover:text-foreground transition-colors ${active ? 'text-foreground' : 'text-muted-foreground'}`}
      data-testid={`sort-${field}`}
      title={active ? `Sorted ${dir === 'asc' ? 'ascending' : 'descending'} — click to ${dir === 'asc' ? 'sort descending' : 'clear'}` : 'Click to sort'}
    >
      <span className="truncate">{children}</span>
      <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${active ? 'opacity-100 text-primary' : 'opacity-70'}`} />
    </button>
  );
}

/**
 * Generic comparator builder.
 * `getter(item)` returns the value to compare; null/undefined sort last.
 */
export const sortBy = (items, getter, dir) => {
  if (!dir) return items;
  const sign = dir === 'asc' ? 1 : -1;
  const out = [...items];
  out.sort((a, b) => {
    const va = getter(a);
    const vb = getter(b);
    const aEmpty = va === null || va === undefined || va === '';
    const bEmpty = vb === null || vb === undefined || vb === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * sign;
  });
  return out;
};
